import React, {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
  useState
} from "react";

import * as d3 from "d3";

import { ComparisonSide, ComparisonSideTimeFrame } from "../types";

import * as S from "./TimelineSelector.style";
import TimelineSelectorCursor from "./TimelineSelectorCursor";

type Props = {
  side: ComparisonSide;
  comparisonSideTimeframe: ComparisonSideTimeFrame;
  onComparisonSideTimeframeChanged: (newValue: ComparisonSideTimeFrame) => void;
  xScale: d3.ScaleTime<number, number, never>;
  yScale: d3.ScaleLinear<number, number, never>;
  height: number;
  onHighlight: () => void;
};

const TimelineSelector = ({
  side,
  comparisonSideTimeframe,
  onComparisonSideTimeframeChanged,
  xScale,
  yScale,
  onHighlight
}: Props) => {
  const ref = useRef<SVGGElement>(null);
  const [highlight, setHighlight] = useState(false);

  const [y1, y2] = [yScale.range()[0], yScale.range()[1]];
  const [minX, maxX] = [xScale.range()[0], xScale.range()[1]];
  const [offset, setOffset] = useState<[number, number]>([0, 0]);

  const computeLeftAndRight = useCallback(
    (
      comparisonSideTimeframe: ComparisonSideTimeFrame,
      offset: [number, number]
    ) => {
      const fromX = xScale(comparisonSideTimeframe.from);
      const toX = xScale(comparisonSideTimeframe.to);
      if (offset[1] === offset[0]) {
        // nothing changed
        if (offset[0] === 0) {
          return [fromX, toX];
        }

        // When sliding the rectangle, both sides are blocked by the limits.
        // So here we calculate the distance bounded to the limits
        const distance = Math.min(
          Math.max(offset[0], minX - fromX),
          maxX - toX
        );

        return [fromX + distance, toX + distance];
      }

      const x1 = Math.min(maxX, Math.max(minX, fromX + offset[0]));
      const x2 = Math.min(maxX, Math.max(minX, toX + offset[1]));

      // flip flap the left and right corners
      const left = x1 <= x2 ? x1 : x2;
      const right = x1 <= x2 ? x2 : x1;

      return [left, right];
    },
    [maxX, minX, xScale]
  );

  const [left, right] = computeLeftAndRight(comparisonSideTimeframe, offset);

  const handleMouseEnter = useCallback(() => {
    setHighlight(true);
    onHighlight();
  }, [onHighlight]);
  const handleMouseLeave = useCallback(() => {
    setHighlight(false);
  }, []);

  const handleMouseDown = useCallback(
    (e: ReactPointerEvent<SVGRectElement>, drag: "left" | "right" | "area") => {
      const target = e.target as SVGElement;
      const reference = e.clientX;

      target.onpointermove = (e: PointerEvent) => {
        const distance = e.clientX - reference;
        if (drag === "left") {
          setOffset([distance, 0]);
        } else if (drag === "right") {
          setOffset([0, distance]);
        } else {
          setOffset([distance, distance]);
        }
      };
      target.onpointerup = (e: PointerEvent) => {
        target.onpointermove = null;
        target.releasePointerCapture(e.pointerId);
        const distance = e.clientX - reference;
        const [left, right] = computeLeftAndRight(comparisonSideTimeframe, [
          drag !== "right" ? distance : 0,
          drag !== "left" ? distance : 0
        ]);
        onComparisonSideTimeframeChanged({
          from: xScale.invert(left),
          to: xScale.invert(right)
        });
        setOffset([0, 0]);
      };

      target.setPointerCapture(e.pointerId);
    },
    [
      comparisonSideTimeframe,
      computeLeftAndRight,
      onComparisonSideTimeframeChanged,
      xScale
    ]
  );

  return (
    <g
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      opacity={highlight ? 1 : 0.6}
      y1={y1}
      y2={y2}
    >
      <S.Background
        x={left}
        width={right - left}
        y={y2}
        height={y1 - y2}
        onPointerDown={e => handleMouseDown(e, "area")}
        side={side}
        highlight={highlight}
      />
      <TimelineSelectorCursor
        left={left}
        top={y2}
        height={y1 - y2}
        onPointerDown={e => handleMouseDown(e, "left")}
        side={side}
      />
      <TimelineSelectorCursor
        left={right - 8}
        top={y2}
        height={y1 - y2}
        onPointerDown={e => handleMouseDown(e, "right")}
        side={side}
      />
    </g>
  );
};

export default TimelineSelector;
